import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocalizedPath } from "@/i18n/useLocalizedPath";
import { fadeUp, stagger, viewportOnce } from "./motion-presets";

const BULLET_KEYS = ["tracking", "payment", "installed"] as const;

const STORE_LINKS = [
  { key: "appStore", href: "https://apps.apple.com/us/app/workloop-services/id6761512706" },
  { key: "googlePlay", href: "https://play.google.com/store/apps/details?id=com.cmacha2.workloopclient" },
] as const;

export const CustomerAppSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("landing");
  const lp = useLocalizedPath();

  return (
    <section id="customer-app" className="relative py-24 px-4 overflow-hidden">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={stagger}
        className="container mx-auto max-w-6xl grid md:grid-cols-2 gap-12 md:gap-16 items-center"
      >
        <div>
          <motion.span
            variants={fadeUp}
            className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
          >
            {t("customerApp.eyebrow")}
          </motion.span>
          <motion.h2
            variants={fadeUp}
            className="mt-3 font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground"
          >
            {t("customerApp.title")}
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-5 text-muted-foreground leading-relaxed">
            {t("customerApp.body")}
          </motion.p>

          <motion.ul variants={fadeUp} className="mt-7 space-y-3">
            {BULLET_KEYS.map((key) => (
              <li key={key} className="flex items-start gap-3 text-sm text-foreground/85">
                <span className="mt-0.5 inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary/10 shrink-0">
                  <Check className="h-3 w-3 text-primary" />
                </span>
                {t(`customerApp.bullets.${key}`)}
              </li>
            ))}
          </motion.ul>

          <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              onClick={() => navigate(lp("/auth") + "?mode=signup")}
              className="btn-gradient text-white border-0 h-10 px-5 gap-2"
            >
              {t("customerApp.cta")} <ArrowRight className="h-4 w-4" />
            </Button>
            {STORE_LINKS.map(({ key, href }) => (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card/40 px-3.5 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                {t(`customerApp.stores.${key}`)} <ArrowUpRight className="h-3 w-3" />
              </a>
            ))}
          </motion.div>
        </div>

        <motion.div variants={fadeUp} className="relative flex justify-center gap-5 md:gap-7">
          <div className="w-40 sm:w-48 md:w-52 -rotate-2 mt-8">
            <div className="rounded-[1.75rem] border-[5px] border-zinc-900 bg-zinc-900 overflow-hidden shadow-2xl shadow-black/40">
              <img
                src="/screenshots/customer-app-home.jpg"
                alt={t("customerApp.imageAltHome")}
                width="480"
                height="1064"
                loading="lazy"
                decoding="async"
                className="block w-full h-auto"
              />
            </div>
          </div>
          <div className="w-40 sm:w-48 md:w-52 rotate-2">
            <div className="rounded-[1.75rem] border-[5px] border-zinc-900 bg-zinc-900 overflow-hidden shadow-2xl shadow-black/40">
              <img
                src="/screenshots/customer-app-booking.jpg"
                alt={t("customerApp.imageAltBooking")}
                width="480"
                height="1064"
                loading="lazy"
                decoding="async"
                className="block w-full h-auto"
              />
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};
